import { createHttpClient } from '@adapters/HttpClient';
import { CouriersApiAdapter } from '@modules/Orders/List/adapters/CouriersApiAdapter';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import type { DateRange } from 'react-day-picker';

interface OrderPreparationFiltersState {
  status: string[];
  type: string[];
  location_ids: string[];
  courier: string[];
  created_at: DateRange | undefined;
  qty: { min: string; max: string };
  qty_done: { min: string; max: string };
}

interface UseOrderPreparationFiltersProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const useOrderPreparationFilters = ({ initialConditions = [], onChange }: UseOrderPreparationFiltersProps) => {
  const [filters, setFilters] = useState<OrderPreparationFiltersState>(() => parseConditionsToState(initialConditions));

  const httpClient = createHttpClient(apiClientWH);
  const couriersAdapter = new CouriersApiAdapter(httpClient);

  const { data: couriers = [], isLoading: isLoadingCouriers } = useQuery({
    queryKey: ['order-preparation-filters-couriers'],
    queryFn: async () => {
      const list = await couriersAdapter.getAll();
      return list.map((courier) => ({ value: courier.id, label: courier.name }));
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const convertStateToConditions = useCallback((state: OrderPreparationFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    if (state.status.length > 0) {
      conditions.push({
        column: 'status',
        action: 'in',
        value: state.status,
      });
    }

    if (state.type.length > 0) {
      conditions.push({
        column: 'type',
        action: 'in',
        value: state.type,
      });
    }

    if (state.location_ids.length > 0) {
      conditions.push({
        column: 'location_id',
        action: 'in',
        value: state.location_ids,
      });
    }

    if (state.courier.length > 0) {
      conditions.push({
        column: 'courier_id',
        action: 'in',
        value: state.courier,
      });
    }

    if (state.created_at?.from && state.created_at?.to) {
      conditions.push({
        column: 'created_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.created_at.from),
      });
      conditions.push({
        column: 'created_at',
        action: 'less_equal',
        value: formatDateForAPI(state.created_at.to),
      });
    }

    if (state.qty.min) {
      conditions.push({
        column: 'qty',
        action: 'greater_equal',
        value: state.qty.min,
      });
    }
    if (state.qty.max) {
      conditions.push({
        column: 'qty',
        action: 'less_equal',
        value: state.qty.max,
      });
    }

    if (state.qty_done.min) {
      conditions.push({
        column: 'qty_done',
        action: 'greater_equal',
        value: state.qty_done.min,
      });
    }
    if (state.qty_done.max) {
      conditions.push({
        column: 'qty_done',
        action: 'less_equal',
        value: state.qty_done.max,
      });
    }

    return conditions;
  }, []);

  const updateFilters = useCallback(
    (updates: Partial<OrderPreparationFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  const handleStatusChange = useCallback((values: string[]) => updateFilters({ status: values }), [updateFilters]);
  const handleTypeChange = useCallback(
    (values: string[]) => {
      setFilters((prev) => {
        let nextType = values;

        // Tipo de preparación debe ser selección única: Picking o Packing masivo
        if (values.length > 1) {
          const addedValue = values.find((value) => !prev.type.includes(value));
          nextType = addedValue ? [addedValue] : [values[values.length - 1]];
        }

        const newState = { ...prev, type: nextType };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [onChange, convertStateToConditions],
  );
  const handleCreatedAtChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ created_at: range }),
    [updateFilters],
  );
  const handleLocationIdsChange = useCallback(
    (values: string[]) => updateFilters({ location_ids: values }),
    [updateFilters],
  );
  const handleCourierChange = useCallback((values: string[]) => updateFilters({ courier: values }), [updateFilters]);
  const handleQtyChange = useCallback(
    (type: 'min' | 'max', value: string) => {
      setFilters((prev) => {
        const newQty = { ...prev.qty, [type]: value };
        const newState = { ...prev, qty: newQty };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [onChange, convertStateToConditions],
  );
  const handleQtyDoneChange = useCallback(
    (type: 'min' | 'max', value: string) => {
      setFilters((prev) => {
        const newQtyDone = { ...prev.qty_done, [type]: value };
        const newState = { ...prev, qty_done: newQtyDone };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [onChange, convertStateToConditions],
  );

  const clearAllFilters = useCallback(() => {
    const emptyState: OrderPreparationFiltersState = {
      status: [],
      type: [],
      location_ids: [],
      courier: [],
      created_at: undefined,
      qty: { min: '', max: '' },
      qty_done: { min: '', max: '' },
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  useEffect(() => {
    setFilters(parseConditionsToState(initialConditions));
  }, [initialConditions]);

  return {
    filters,
    handleStatusChange,
    handleTypeChange,
    handleLocationIdsChange,
    handleCourierChange,
    handleCreatedAtChange,
    handleQtyChange,
    handleQtyDoneChange,
    clearAllFilters,
    courierOptions: couriers,
    isLoadingCouriers,
  };
};

function parseConditionsToState(conditions: FilterConditionOutput[]): OrderPreparationFiltersState {
  const state: OrderPreparationFiltersState = {
    status: [],
    type: [],
    location_ids: [],
    courier: [],
    created_at: undefined,
    qty: { min: '', max: '' },
    qty_done: { min: '', max: '' },
  };

  const byColumn: Record<string, FilterConditionOutput[]> = {};
  conditions.forEach((c) => {
    if (!byColumn[c.column]) byColumn[c.column] = [];
    byColumn[c.column].push(c);
  });

  const getArray = (col: string) => {
    const c = byColumn[col]?.find((x) => x.action === 'in');
    if (!c?.value) return [];
    return Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
  };

  state.status = getArray('status');
  state.type = getArray('type');
  state.location_ids = getArray('location_id');
  state.courier = getArray('courier_id');

  const getRange = (col: string) => {
    const min = byColumn[col]?.find((c) => c.action === 'greater_equal');
    const max = byColumn[col]?.find((c) => c.action === 'less_equal');
    return {
      min: min ? String(min.value) : '',
      max: max ? String(max.value) : '',
    };
  };
  state.qty = getRange('qty');
  state.qty_done = getRange('qty_done');

  const dateMin = byColumn['created_at']?.find((c) => c.action === 'greater_equal');
  const dateMax = byColumn['created_at']?.find((c) => c.action === 'less_equal');
  if (dateMin?.value && dateMax?.value) {
    state.created_at = {
      from: new Date(String(dateMin.value)),
      to: new Date(String(dateMax.value)),
    };
  }

  return state;
}
